import { useEffect, useRef } from "react";
import { Trans } from "react-i18next";
import ReactPlayer from "react-player/youtube";
import { useNavigate } from "react-router-dom";

import usePostCheckoutState from "@hooks/usePostCheckoutState";

import { ClassesEnum } from "@/ts/enums/ClassesEnum";
import { getTrackingId } from "@/utils/utils";
import { useAtom, useSetAtom } from "jotai";

import { Button, Flex, Image, useMediaQuery } from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { CheckoutPagesEnum } from "@ts/enums/CheckoutPagesEnum";

import article1Png from "@assets/img/article-img-1.png";
import article2Png from "@assets/img/article-img-2.png";
import article3Png from "@assets/img/article-img-3.png";
import paymentSuccessSvg from "@assets/img/payment-success.svg";

import {
    canShowPaymentSuccessPageAtom,
    checkoutAtom,
    checkoutTotalAmountAtom,
    couponAtom,
    userCheckoutStateTokenAtom
} from "@atoms/checkoutAtom";

import ArticleItem from "@components/ArticleItem/ArticleItem";
import { Text } from "@components/Text";

const articles = [
    {
        id: 1,
        coverImage: article1Png,
        category: "Advice",
        title: "Homeschool Record Keeping - How To Save Time"
    },
    {
        id: 2,
        coverImage: article2Png,
        category: "Advice",
        title: "8 Tips for Socialisation Success: Unleashing the Power of Connection in Homeschooling"
    },
    {
        id: 3,
        coverImage: article3Png,
        category: "Advice",
        title: "Creating a Homeschool Schedule that Works!"
    }
];

const PaymentSuccessContainer = () => {
    const navigate = useNavigate();

    const { checkoutStateMutate } = usePostCheckoutState();

    // const [paymentData, setPaymentData] = useState<{firstName: string, email: string}>({firstName: "", email: ""}):

    const paymentDataRef = useRef<{ firstName: string; email: string }>({
        email: "",
        firstName: ""
    });

    const [checkout, setCheckout] = useAtom(checkoutAtom);
    const [canShowPaymentSuccessPage, setCanShowPaymentSuccessPage] = useAtom(
        canShowPaymentSuccessPageAtom
    );
    const setUserCheckoutStateToken = useSetAtom(userCheckoutStateTokenAtom);
    const setCoupon = useSetAtom(couponAtom);
    const setCheckoutTotal = useSetAtom(checkoutTotalAmountAtom);
    // const setStudentIdForCheckout = useSetAtom(studentIdForCheckoutAtom);

    const [isLargerThan900] = useMediaQuery("(min-width: 900px)");

    //  TODO: Refactor this to clean out the existing atom values from the jotai store using a provider
    const onResetCheckout = () => {
        setCheckout(prev => ({
            ...prev,
            user: { email: "", firstName: "", receiveOfferNews: false },
            productFamily: null,
            grade: null,
            plan: null,
            addon: null,
            charge: null,
            student: null,
            selectedStudent: null
        }));
        setUserCheckoutStateToken(null);
        setCoupon(null);
        setCheckoutTotal(0);
        // setStudentIdForCheckout(null);
    };

    useEffect(() => {
        onResetCheckout();
        if (canShowPaymentSuccessPage) {
            setCheckout(prev => ({
                ...prev,
                nextAttemptPage: CheckoutPagesEnum.PAYMENT_SUCCESS_PAGE
            }));
            checkoutStateMutate();
        } else {
            navigate(CheckoutPageRoutesEnum.USER_INFO_PAGE);
        }
        return () => setCanShowPaymentSuccessPage(false);
    }, [canShowPaymentSuccessPage]);

    useEffect(() => {
        if (!!checkout.user.email && !!checkout.user.firstName) {
            paymentDataRef.current.email = checkout.user.email;
            paymentDataRef.current.firstName = checkout.user.firstName;
        }
    }, [checkout]);

    return (
        <Flex
            direction="column"
            justify="center"
            align="center"
            mt={12}
            gap={20}
            className={ClassesEnum.PaymentSuccessContainer}
        >
            <Flex
                gap={10}
                direction="column"
                justify="center"
                align="center"
                px={4}
                w={["full", null, null, 800]}
            >
                <Text
                    type="heading"
                    textAlign="center"
                    data-tracking-id={getTrackingId(
                        [ClassesEnum.PaymentSuccessContainer, ClassesEnum.Text],
                        "paymentSuccessThank"
                    )}
                >
                    <Trans
                        defaults="Thanks, {{ firstName }} and welcome to Euka!"
                        values={{ firstName: paymentDataRef.current.firstName }}
                    />
                </Text>
                <Image
                    pointerEvents="none"
                    userSelect="none"
                    src={paymentSuccessSvg}
                    w={280}
                    h={180}
                />
                <Text textAlign="center" type="subHeading">
                    <Trans>
                        You will receive an email with account details shortly.
                    </Trans>
                </Text>
                <Text textAlign="center">
                    <Trans
                        defaults="Upon confirmation of your successful payment, you will receive an email sent to {{ email }} with your account details and steps to complete enrolment."
                        values={{ email: paymentDataRef.current.email }}
                    />
                </Text>
                <Button colorScheme="ebonyClay" w={["full", null, 320]}>
                    Watch Euka welcome video
                </Button>
            </Flex>
            <Flex
                bgColor="gray.100"
                w="full"
                justify="center"
                align="center"
                p={[5, null, null, 14]}
                direction="column"
                gap={12}
            >
                <Text type="heading" textAlign="center" mt={[9, null, null, 0]}>
                    <Trans>
                        Discover some Euka Essentials to help you prepare
                    </Trans>
                </Text>
                <ReactPlayer
                    style={{
                        boxShadow: "-1px 28px 40px -15px rgba(22,39,54,0.39)"
                    }}
                    url="https://www.youtube.com/watch?v=LG3L6J07g0Q"
                    width={!isLargerThan900 ? "100%" : 800}
                    height={!isLargerThan900 ? 300 : 450}
                    data-testid="player"
                />
                <Flex gap={7} mt={5} wrap="wrap" justify="center">
                    {articles.map(a => (
                        <ArticleItem
                            key={a.id}
                            coverImage={a.coverImage}
                            category={a.category}
                            title={a.title}
                        />
                    ))}
                </Flex>
                <Button colorScheme="ebonyClay" variant="outline">
                    View all resources
                </Button>
            </Flex>
        </Flex>
    );
};

export default PaymentSuccessContainer;
